import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import { ROUTES_CONFIG } from "src/constants/routesConfig";

interface SuccessProps {
  childPurchaseOrderId: string;
  asn?: string;
  onClose?: () => void;
}

interface WarningProps {
  childPurchaseOrderId: string;
  onCancel: () => void;
  onConfirm: () => void;
}

interface ErrorProps {
  onRetry?: () => void;
  onClose?: () => void;
}

const Success: React.FC<SuccessProps> = ({ childPurchaseOrderId, asn, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-3">
            <CheckCircle2 className="w-6 h-6 text-success-600" />
            <h2 className="text-lg font-semibold text-gray-900">Child Purchase Order Confirmed</h2>
          </div>
          {onClose && (
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
              &times;
            </button>
          )}
        </div>

        <div className="p-5 space-y-4">
          <div className="bg-success-50 border border-success-200 rounded-lg p-4 space-y-2">
            <p className="text-sm text-gray-700">
              The child purchase order has been confirmed and the ASN sent to GXO.
            </p>
            <p className="text-sm text-gray-700">
              <span className="font-semibold text-success-700">Child PO ID: {childPurchaseOrderId}</span>
            </p>
            {asn && (
              <p className="text-sm text-gray-700">
                <span className="font-semibold text-success-700">ASN: {asn}</span>
              </p>
            )}
          </div>

          {/* Home Button */}
          <div className="flex justify-center pt-2">
            <Link to={ROUTES_CONFIG.home.path}>
              <button className="px-6 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors text-sm font-medium">
                Home
              </button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const ConfirmWarning: React.FC<WarningProps> = ({ childPurchaseOrderId, onConfirm, onCancel }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="w-6 h-6 text-warning-600" />
            <h2 className="text-lg font-semibold text-gray-900">Confirm Child Purchase Order</h2>
          </div>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
            &times;
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="bg-warning-50 border border-warning-200 rounded-lg p-4">
            <div className="flex items-start">
              <AlertTriangle className="w-5 h-5 text-warning-600 mt-0.5 mr-2 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-warning-800 mb-1">
                  Are you sure you want to confirm this child purchase order?
                </p>
                <p className="text-sm text-warning-700">ID: {childPurchaseOrderId}</p>
              </div>
            </div>
          </div>
          <p className="text-sm text-gray-700 text-center font-medium">
            The ASN will be sent to GXO and the PO will move to In Progress
          </p>
        </div>

        {/* Footer Buttons */}
        <div className="flex justify-end space-x-3 p-5 border-t border-gray-200 bg-gray-50">
          <button
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors text-sm font-medium"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 bg-success-600 text-white rounded-md hover:bg-success-700 transition-colors text-sm font-medium"
            onClick={onConfirm}
          >
            Confirm &amp; Send to GXO
          </button>
        </div>
      </div>
    </div>
  );
};

const Error: React.FC<ErrorProps> = ({ onRetry, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-3">
            <XCircle className="w-6 h-6 text-error-600" />
            <h2 className="text-lg font-semibold text-gray-900">Confirmation Failed</h2>
          </div>
          {onClose && (
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
              &times;
            </button>
          )}
        </div>

        <div className="p-5 space-y-4">
          <p className="text-sm text-gray-700 text-center">
            An error occurred while confirming the child purchase order. Please try again.
          </p>

          {/* Footer Buttons */}
          <div className="flex justify-center space-x-3 pt-2">
            {onClose && (
              <button
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors text-sm font-medium"
                onClick={onClose}
              >
                Close
              </button>
            )}
            {onRetry && (
              <button
                className="px-6 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors text-sm font-medium"
                onClick={onRetry}
              >
                Retry
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const ConfirmChildPurchaseOrderModals = {
  Success,
  ConfirmWarning,
  Error
};

export { ConfirmChildPurchaseOrderModals };
